/**
 * How loud this MACHINE is, right now.
 *
 * Deliberately device-local, like `radarPrefs.ts` and `home.ts`. The tenant decides
 * which tone a tornado warning makes — that is config, and it is audited. Whether the
 * speakers on this particular desk are muted for the next half hour is not config: it
 * is one operator turning down their own volume, and pushing it to the server would let
 * them silence the dispatch floor, the EOC and the wallboard by accident.
 *
 * Keyed by tenant so a super_admin switching counties does not carry one county's mute
 * into another.
 */

/** Offered snooze lengths, in minutes. */
export const SNOOZE_CHOICES: Array<{ minutes: number; label: string }> = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 hour' },
  { minutes: 240, label: '4 hours' },
  { minutes: 480, label: '8 hours' },
];

export interface SoundPrefs {
  /** Muted with no end date, until turned back on by hand. */
  muted: boolean;
  /** Epoch ms at which a timed mute lapses. Null when there is no timed mute. */
  mutedUntil: number | null;
  /** 0..1 */
  volume: number;
}

export const SOUND_PREFS_DEFAULT: SoundPrefs = { muted: false, mutedUntil: null, volume: 0.7 };

const KEY = (tenantId: string) => `ww.sound.${tenantId}`;

function clean(raw: unknown): SoundPrefs {
  const c = raw as Partial<SoundPrefs> | null;
  if (!c || typeof c !== 'object') return { ...SOUND_PREFS_DEFAULT };
  const volume =
    typeof c.volume === 'number' && Number.isFinite(c.volume)
      ? Math.min(1, Math.max(0, c.volume))
      : SOUND_PREFS_DEFAULT.volume;
  // A mutedUntil in the past is harmless but noisy to reason about; drop it on read.
  const until =
    typeof c.mutedUntil === 'number' && Number.isFinite(c.mutedUntil) && c.mutedUntil > Date.now()
      ? c.mutedUntil
      : null;
  return { muted: c.muted === true, mutedUntil: until, volume };
}

export function getSoundPrefs(tenantId: string | null | undefined): SoundPrefs {
  if (!tenantId) return { ...SOUND_PREFS_DEFAULT };
  try {
    const raw = window.localStorage.getItem(KEY(tenantId));
    return raw ? clean(JSON.parse(raw) as unknown) : { ...SOUND_PREFS_DEFAULT };
  } catch {
    return { ...SOUND_PREFS_DEFAULT };
  }
}

export function setSoundPrefs(
  tenantId: string | null | undefined,
  patch: Partial<SoundPrefs>,
): SoundPrefs {
  const next = clean({ ...getSoundPrefs(tenantId), ...patch });
  if (!tenantId) return next;
  try {
    window.localStorage.setItem(KEY(tenantId), JSON.stringify(next));
  } catch {
    /* storage unavailable; the choice still holds for this session */
  }
  return next;
}

/** True when nothing should sound on this device at this instant. */
export function isSilenced(prefs: SoundPrefs, now = Date.now()): boolean {
  return prefs.muted || (prefs.mutedUntil !== null && prefs.mutedUntil > now);
}

/** Milliseconds left on a timed mute, or 0. Used to count the mute down on screen. */
export function silenceRemainingMs(prefs: SoundPrefs, now = Date.now()): number {
  if (prefs.muted || prefs.mutedUntil === null) return 0;
  return Math.max(0, prefs.mutedUntil - now);
}

/**
 * A short "23 min" string for a running mute.
 *
 * Rounds UP, on purpose: a mute that says 1 min and lasts 90 seconds is one an operator
 * can rely on. One that says 0 min and is still silent is one they cannot.
 */
export function formatSilenceRemaining(ms: number): string {
  const mins = Math.ceil(ms / 60_000);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
